import { Injectable } from '@angular/core';
import { AdminMessages } from '@app/admin/admin.messages';
import { Page } from '@app/models/api/page.model';
import { PagingParams } from '@app/models/api/paging-params.model';
import { SortingParams } from '@app/models/api/sorting-params.model';
import { UserMinimal } from '@app/models/user/user-minimal.model';
import { Globals } from '@app/shared/globals/globals';
import { NotifyUtils } from '@app/shared/utils/notify.utils';
import { Observable, firstValueFrom } from 'rxjs';
import { RoleAPIService } from '../api/role-api.service';
import { CreateRoleDto } from '../model/create-role.model';
import { ProvisionedPosition } from '../model/provisioned-position.model';
import { Role } from '../model/role.model';
import { InFlightRegistry } from '@app/shared/utils/in-flight-registry';

// Single-slot guard key for the file import, which allows only one concurrent run.
const IMPORT_FILE_KEY = 'import-file';

@Injectable({
  providedIn: 'root'
})
export class RoleBusinessService {

  // key -> the write currently in flight for that key. A duplicate call while one is in flight is
  // coalesced onto the same promise (see InFlightRegistry) rather than rejected.
  updating: InFlightRegistry;

  constructor(
    public globals: Globals,
    private roleAPI: RoleAPIService,
    private notifyUtils: NotifyUtils
  ) {
    this.updating = new InFlightRegistry();
  }

  get(
    ids: number[],
    name: string,
    grade: string,
    competencyIds: number[]
  ): Observable<Role[]> {
    return this.roleAPI.get(ids, name, grade, competencyIds);
  }

  getPaginated(
    pagingParams: PagingParams,
    sortingParams: SortingParams,
    ids: number[],
    name: string,
    grade: string,
    competencyIds: number[]
  ): Observable<Page<Role>> {
    return this.roleAPI.getPaginated(pagingParams, sortingParams, ids, name, grade, competencyIds);
  }

  create(createDto: CreateRoleDto): Promise<Role> {
    return firstValueFrom(this.roleAPI.create(createDto))
      .then(res => {
        this.notifyUtils.notify(AdminMessages.ROLES.SUCCESS_ROLE_CREATE);
        return res;
      });
  }

  update(id: number, updateDto: CreateRoleDto): Promise<Role> {
    return this.updating.run(id, () =>
      firstValueFrom(this.roleAPI.update(id, updateDto))
        .then(res => {
          this.notifyUtils.notify(AdminMessages.ROLES.SUCCESS_ROLE_UPDATE);

          if (this.globals.user.position && this.globals.user.position.id === id) {
            this.globals.user.position = res;
          }

          return res;
        })
    );
  }

  delete(id: number): Promise<Role> {
    return this.updating.run(id, () =>
      firstValueFrom(this.roleAPI.delete(id))
        .then(res => {
          this.notifyUtils.notify(AdminMessages.ROLES.SUCCESS_DELETE_ROLE_SINGLE);

          if (this.globals.user.position && this.globals.user.position.id === id) {
            this.globals.user.position = null;
          }

          return res;
        })
    );
  }

  deleteMultiple(ids: number[]): Promise<Role[]> {
    return this.updating.run(ids.join(','), () =>
      firstValueFrom(this.roleAPI.deleteMultiple(ids))
        .then(res => {
          this.notifyUtils.notify(AdminMessages.ROLES.SUCCESS_DELETE_ROLE_MULTIPLE);

          if (this.globals.user.position && ids.includes(this.globals.user.position.id)) {
            this.globals.user.position = null;
          }

          return res;
        })
    );
  }

  importFile(file: File): Promise<Role[]> {
    return this.updating.run(IMPORT_FILE_KEY, () =>
      firstValueFrom(this.roleAPI.importRoles(file))
        .then(res => {
          this.notifyUtils.notify(AdminMessages.ROLES.SUCCESS_IMPORT_ROLES);
          return res;
        })
    );
  }

  getAllUsersAssignedToRole(id: number): Observable<UserMinimal[]> {
    return this.roleAPI.getAllUsersAssignedToRole(id);
  }

  getAllProvisionedPositions(): Observable<ProvisionedPosition[]> {
    return this.roleAPI.getAllProvisionedPositions();
  }

  getProvisionedPositionByPositionId(id: number): Observable<ProvisionedPosition> {
    return this.roleAPI.getProvisionedPositionByPositionId(id);
  }

  searchRoles(searchTerm: string): Promise<Role[]> {
    return firstValueFrom(this.roleAPI.searchRoles(searchTerm));
  }
}
